<template>
    <div class="search" :class="{ 'is-open': showResults }">
        <svg class="search__icon" width="14" height="14" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
            <circle cx="11" cy="11" r="7"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>

        <input
            ref="input"
            v-model="query"
            type="text"
            class="search__input"
            placeholder="Search products…"
            autocomplete="off"
            @input="onInput"
            @keydown.enter="goToSearch"
            @keydown.escape="close"
            @keydown.down.prevent="moveDown"
            @keydown.up.prevent="moveUp"
            @focus="onFocus"
        >

        <button
            v-if="query"
            class="search__clear"
            @click="clear"
        >✕</button>

        <!-- Results dropdown -->
        <div v-if="showResults" class="search__dropdown">

            <div v-if="loading" class="search__loading">
                <span class="search__dots">
                    <span></span><span></span><span></span>
                </span>
                Searching…
            </div>

            <template v-else-if="results.length">
                <a v-for="( item, i ) in results"
                    :key="item.id"
                    :href="baseURL + '/product/' + item.slug"
                    class="search__result"
                    :class="{ 'is-active': activeIndex === i }"
                    @mouseenter="activeIndex = i"
                >
                    <div class="search__result-icon">
                        <img
                            v-if="item.image_thumb"
                            :src="item.image_thumb"
                            :alt="item.name"
                        >
                        <span v-else>{{ item.emoji || '📦' }}</span>
                    </div>
                    <div class="search__result-text">
                        <div class="search__result-name">{{ item.name }}</div>
                        <div class="search__result-price">{{ formatMoney( item.sale_price ) }}</div>
                    </div>
                </a>

                <a
                    v-if="resultsTotal > queryLimit"
                    :href="baseURL + '/products?search=' + encodeURIComponent( query )"
                    class="search__see-all"
                >
                    See all results for "{{ query }}" →
                </a>
            </template>

            <div v-else-if="searched" class="search__empty">
                No products found for "{{ query }}"
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'SearchBar',

    data() {
        return {
            query       : '',
            queryLimit  : 10,
            results     : [],
            resultsTotal: 0,
            loading     : false,
            searched    : false,
            showResults : false,
            activeIndex : -1,
            timer       : null,
            baseURL     : window.baseURL || '',
        };
    },

    mounted() {
        document.addEventListener( 'click', this.onClickOutside );
    },

    beforeUnmount() {
        document.removeEventListener( 'click', this.onClickOutside );
    },

    methods: {

        onInput() {

            clearTimeout( this.timer );

            this.activeIndex = -1;

            if( this.query.trim().length < 2 ) {

                this.results     = [];
                this.searched    = false;
                this.showResults = false;

                return;
            }

            this.timer = setTimeout( () => this.search(), 500 );
        },

        async search() {

            this.loading     = true;
            this.showResults = true;

            try {
                const response = await this.$http.get( '/products/list', {
                    params: {
                        search: this.query.trim(),
                        limit:  this.queryLimit,
                    },
                });
                const { status, data } = response?.data || {};

                if( status === 'success' ) {
                    this.results = data.items || [];
                    this.resultsTotal = data.total || 0;
                }
            } catch( e ) {
                this.results = [];
            } finally {
                this.loading  = false;
                this.searched = true;
            }
        },

        goToSearch() {
            if( this.activeIndex >= 0 && this.results[ this.activeIndex ] ) {
                window.location.href = this.baseURL + '/product/' + this.results[ this.activeIndex ].slug;
                return;
            }

            if( this.query.trim() )
                window.location.href = this.baseURL + '/products?search=' + encodeURIComponent( this.query.trim() );
        },

        moveDown() {
            if( this.activeIndex < this.results.length - 1 )
                this.activeIndex++;
        },

        moveUp() {
            if( this.activeIndex > 0 )
                this.activeIndex--;
        },

        onFocus() {
            if( this.results.length && this.query.trim().length >= 2 )
                this.showResults = true;
        },

        close() {
            this.showResults = false;
            this.activeIndex = -1;
        },

        clear() {
            this.query       = '';
            this.results     = [];
            this.searched    = false;
            this.showResults = false;

            this.$refs.input.focus();
        },

        onClickOutside( e ) {
            if( ! this.$el.contains( e.target ) )
                this.close();
        },

        formatMoney( cents ) {
            if( ! cents && cents !== 0 )
                return '';

            const d = cents / 100;

            return '$' + ( Number.isInteger( d ) ? d : d.toFixed( 2 ) );
        },
    },
};
</script>